import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, Activity, Heart, Brain, Zap, Wind, Play, ExternalLink, Instagram } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

const MobilityMovementPage = () => {
  const mobilityAreas = [
    {
      title: "Colonne Vertébrale",
      description: "La colonne vertébrale est l'axe central du corps. Sa mobilité permet les mouvements de flexion, extension, rotation et inclinaison latérale, essentiels pour une respiration complète.",
      icon: Activity,
      color: "from-sky-500 to-sky-600"
    },
    {
      title: "Articulations Costo-Vertébrales",
      description: "Ces articulations relient les côtes aux vertèbres. Leur mobilité permet l'expansion thoracique lors de l'inspiration et le retour élastique à l'expiration.",
      icon: Wind,
      color: "from-teal-500 to-emerald-500"
    },
    {
      title: "Articulations Sterno-Costales",
      description: "Situées à l'avant du thorax, elles permettent l'élévation et l'abaissement du sternum, amplifiant la capacité respiratoire.",
      icon: Heart,
      color: "from-sky-500 to-sky-600"
    },
    {
      title: "Nuque et Cervicales",
      description: "La tension cervicale affecte directement les muscles respiratoires accessoires (scalènes, sterno-cléido-mastoïdiens) et peut comprimer le passage de l'air.",
      icon: Brain,
      color: "from-sky-400 to-sky-500"
    },
    {
      title: "Épaules et Ceinture Scapulaire",
      description: "Des épaules enroulées vers l'avant compriment la cage thoracique et limitent l'expansion pulmonaire, favorisant une respiration superficielle.",
      icon: Zap,
      color: "from-sky-400 to-sky-500"
    },
    {
      title: "Bassin et Hanches",
      description: "Le bassin est la base du diaphragme. Sa mobilité influence directement la liberté de mouvement du muscle respiratoire principal.",
      icon: Activity,
      color: "from-sky-500 to-sky-600"
    }
  ];

  const consequences = [
    {
      system: "Système Respiratoire",
      effects: [
        "Respiration plus rapide et superficielle",
        "Déplacement de la respiration dans le thorax et les épaules",
        "Diaphragme maintenu en position inspiratoire (contraction constante)",
        "Impossibilité de se relâcher sur l'expiration",
        "Inspirations plus courtes",
        "Sensation constante de manquer de capacité inspiratoire"
      ],
      color: "bg-rose-50 border-sky-200"
    },
    {
      system: "Système Circulatoire",
      effects: [
        "Compression des vaisseaux sanguins par le tissu conjonctif tendu",
        "Diminution de l'apport sanguin aux cellules",
        "Stagnation veineuse et lymphatique",
        "Mauvaise oxygénation des tissus"
      ],
      color: "bg-slate-50 border-slate-200"
    },
    {
      system: "Système Nerveux",
      effects: [
        "Activation chronique du système sympathique",
        "Perception constante d'inconfort corporel",
        "État d'alerte permanent (hypervigilance)",
        "Difficultés à se détendre et récupérer"
      ],
      color: "bg-sky-50 border-sky-200"
    },
    {
      system: "Système Digestif",
      effects: [
        "Compression des viscères abdominaux",
        "Blocage du péristaltisme intestinal",
        "Troubles digestifs (ballonnements, constipation)",
        "Reflux gastro-œsophagien favorisé"
      ],
      color: "border-sky-200"
    },
    {
      system: "Système Musculaire",
      effects: [
        "Diminution de la force musculaire globale",
        "Fatigue chronique",
        "Tensions et douleurs musculaires",
        "Perte de coordination et de fluidité"
      ],
      color: "bg-sky-50 border-sky-200"
    }
  ];

  const methods = [
    {
      name: "Qi Gong",
      description: "Art énergétique chinois millénaire qui combine mouvements lents, respiration consciente et méditation. Le Qi Gong travaille sur la circulation de l'énergie vitale (Qi) et permet de relâcher les tensions profondes tout en renforçant le corps de manière douce.",
      benefits: [
        "Mouvements fluides et sans effort",
        "Synchronisation respiration-mouvement",
        "Travail sur les méridiens énergétiques",
        "Accessible à tous les âges et conditions"
      ],
      color: "from-sky-500 to-sky-600",
      youtubeUrl: "https://youtube.com/playlist?list=PLM1Xo_tNlIgS1jk6gdYqRW0K0tCpGIU30&si=_XOgnnsp0SFR0UgU"
    },
    {
      name: "Éducation Somatique de Thomas Hanna",
      description: "Méthode développée par Thomas Hanna basée sur la conscience du mouvement. Elle vise à libérer l'amnésie sensori-motrice - la perte de conscience et de contrôle de certains muscles due au stress chronique et aux traumatismes.",
      benefits: [
        "Réapprentissage du contrôle musculaire conscient",
        "Libération des patterns de tension chronique",
        "Mouvements pandiculation (contraction-relâchement)",
        "Restauration de la proprioception"
      ],
      color: "from-sky-500 to-sky-600",
      youtubeUrl: "https://www.youtube.com/playlist?list=PLhEPf0yNvTLN39JiuEpmiO2EyUoQNM3su"
    },
    {
      name: "Culture du Mouvement (Ido Portal)",
      description: "Approche holistique du mouvement développée par Ido Portal, qui considère le mouvement comme un langage universel. Cette méthode intègre des éléments de gymnastique, danse, arts martiaux, et jeu pour développer un corps polyvalent et adaptable.",
      benefits: [
        "Exploration de toutes les possibilités de mouvement",
        "Développement de la créativité corporelle",
        "Travail au sol et en suspension",
        "Restauration des schémas moteurs naturels"
      ],
      color: "from-slate-500 to-slate-600",
      youtubeUrl: "https://www.youtube.com/playlist?list=PLhEPf0yNvTLN39JiuEpmiO2EyUoQNM3su"
    },
    {
      name: "Roye Gold - Mouvement Naturel",
      description: "Approche qui met l'accent sur la redécouverte des mouvements naturels du corps humain. Roye Gold propose une exploration ludique et progressive pour retrouver la fluidité et la liberté de mouvement perdues.",
      benefits: [
        "Retour aux mouvements fondamentaux",
        "Approche progressive et bienveillante",
        "Jeu et exploration comme outils d'apprentissage",
        "Intégration corps-esprit"
      ],
      color: "from-sky-500 to-sky-600",
      youtubeUrl: "https://www.youtube.com/@royegoldi"
    },
    {
      name: "Karimu Samuels - Mouvement Ancestral",
      description: "Karimu Samuels propose une approche qui reconnecte avec les mouvements ancestraux et la sagesse corporelle innée. Son travail intègre des influences africaines, le yoga, et les pratiques somatiques contemporaines.",
      benefits: [
        "Connexion aux racines du mouvement humain",
        "Libération émotionnelle par le mouvement",
        "Rythme et musicalité du corps",
        "Guérison par la danse et l'expression"
      ],
      color: "from-violet-500 to-purple-500",
      instagramUrl: "https://www.instagram.com/karimu_samuels/"
    },
    {
      name: "Systema - Art Martial Russe",
      description: "Le Systema est un art martial russe dont les deux piliers fondamentaux sont la respiration et le relâchement dans le mouvement. Cette discipline enseigne à rester détendu et fluide même sous pression, en utilisant la respiration comme outil principal de gestion du stress et de l'énergie.",
      benefits: [
        "Respiration continue et consciente",
        "Relâchement musculaire constant",
        "Fluidité et adaptabilité du mouvement",
        "Gestion du stress et des situations difficiles"
      ],
      color: "from-slate-600 to-slate-800",
      youtubeUrl: "https://www.youtube.com/@JMSystema/playlists"
    }
  ];

  const exercises = [
    {
      title: "Mobilisation de la Colonne",
      description: "Exercices doux pour réveiller chaque vertèbre et restaurer la fluidité du rachis.",
      videoUrl: "https://www.youtube.com/watch?v=example1",
      duration: "10-15 min"
    },
    {
      title: "Ouverture Thoracique",
      description: "Mouvements pour libérer les articulations costo-vertébrales et sterno-costales.",
      videoUrl: "https://www.youtube.com/watch?v=example2",
      duration: "8-12 min"
    },
    {
      title: "Libération Cervicale",
      description: "Séquence douce pour relâcher les tensions de la nuque et des épaules.",
      videoUrl: "https://www.youtube.com/watch?v=example3",
      duration: "10 min"
    },
    {
      title: "Mobilité du Bassin",
      description: "Exercices pour redonner liberté et fluidité au bassin et aux hanches.",
      videoUrl: "https://www.youtube.com/watch?v=example4",
      duration: "12-15 min"
    },
    {
      title: "Respiration et Mouvement",
      description: "Intégration de la respiration dans le mouvement pour une pratique complète.",
      videoUrl: "https://www.youtube.com/watch?v=example5",
      duration: "15-20 min"
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFFFF", fontFamily: "Figtree, sans-serif" }}>
      <SiteHeaderV2 />
      
      {/* Hero */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <PageTitle
            title="Mobilité et"
            highlight="Mouvement"
            subtitle="Restaurer la liberté de mouvement pour libérer le souffle"
            align="center"
            className="mb-10"
          />

          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 mb-6">
            <div className="w-64 h-64 md:w-80 md:h-80 rounded-full shadow-lg border-4 border-indigo-200 overflow-hidden flex-shrink-0">
              <img 
                src="/mobilite-qigong.jpg"
                alt="Qi Gong - Mobilité et mouvement"
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center center' }}
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-xl text-slate-600 leading-relaxed">
                La liberté de mouvement est la condition préalable à une respiration optimale. 
                Découvrez comment restaurer la mobilité de votre corps pour libérer votre souffle.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="prose prose-lg max-w-none text-slate-600 space-y-6">
            <p className="text-lg leading-relaxed">
              <strong className="text-slate-800">L&apos;enraidissement du tissu conjonctif et de la musculature</strong> constitue 
              un obstacle majeur au bon fonctionnement du système respiratoire. Lorsque les fascias, muscles et articulations 
              perdent leur souplesse naturelle, c&apos;est l&apos;ensemble de l&apos;organisme qui en subit les conséquences.
            </p>
            
            <div className="bg-white/60 backdrop-blur-md rounded-2xl p-6 border border-white/70 shadow-lg">
              <h3 className="text-xl font-medium mb-4 text-slate-900">Le cercle vicieux de la rigidité</h3>
              <p className="text-slate-800 mb-4">
                Quand le corps se rigidifie, les <strong>systèmes circulatoire, nerveux et digestif se trouvent comprimés</strong> 
                par la tension du tissu conjonctif. Cette compression entraîne :
              </p>
              <ul className="space-y-2 text-slate-800">
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 mt-1">•</span>
                  <span>Une <strong>diminution de l&apos;apport sanguin aux cellules</strong>, privant les tissus d&apos;oxygène et de nutriments</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 mt-1">•</span>
                  <span>Un <strong>blocage des viscères</strong>, perturbant la digestion et l&apos;élimination</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 mt-1">•</span>
                  <span>Une <strong>diminution de la force musculaire</strong> par manque d&apos;irrigation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 mt-1">•</span>
                  <span>Une <strong>activation chronique du système sympathique</strong> due à la perception constante d&apos;un inconfort corporel</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/60 backdrop-blur-md rounded-2xl p-6 border border-white/70 shadow-lg">
              <h3 className="text-xl font-medium mb-4 text-slate-900">Impact sur la mécanique respiratoire</h3>
              <p className="text-slate-800 mb-4">
                La mécanique respiratoire étant bloquée, le corps va compenser de manière dysfonctionnelle :
              </p>
              <ul className="space-y-2 text-slate-800">
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 mt-1">→</span>
                  <span>Le corps se met à <strong>respirer plus vite</strong> pour compenser le manque d&apos;efficacité</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 mt-1">→</span>
                  <span>La respiration se <strong>déplace dans le thorax et les épaules</strong>, abandonnant le diaphragme</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 mt-1">→</span>
                  <span>Le diaphragme reste en <strong>position inspiratoire constante</strong>, dans une contraction permanente</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 mt-1">→</span>
                  <span>Le corps ne peut plus <strong>se relâcher sur l&apos;expiration</strong>, maintenant une tension chronique</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-sky-600 mt-1">→</span>
                  <span>Les inspirations deviennent <strong>plus courtes</strong>, avec une sensation constante de <strong>manquer de capacité inspiratoire</strong></span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Mobility Areas */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
              Les Zones Clés de Mobilité
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Six régions anatomiques dont la mobilité est essentielle pour une respiration libre et complète.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mobilityAreas.map((area, index) => (
              <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${area.color}`} />
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${area.color} flex items-center justify-center mb-4`}>
                    <area.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-lg text-slate-800 mb-2">{area.title}</h3>
                  <p className="text-slate-600 text-sm">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Consequences */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
              Conséquences de la Rigidité sur les Systèmes du Corps
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              L&apos;enraidissement affecte l&apos;ensemble de l&apos;organisme, créant un cercle vicieux de dysfonctionnements.
            </p>
          </div>
          
          <div className="space-y-6">
            {consequences.map((item, index) => (
              <Card key={index} className={`border-2 ${item.color}`}>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg text-slate-800 mb-4">{item.system}</h3>
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {item.effects.map((effect, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600">
                        <span className="text-slate-400 mt-1">•</span>
                        {effect}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Methods */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
              Méthodes pour Retrouver la Mobilité
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Des approches douces et progressives pour libérer le corps sans le stresser, 
              issues de traditions ancestrales et de recherches contemporaines.
            </p>
          </div>
          
          <div className="space-y-8">
            {methods.map((method, index) => (
              <Card key={index} className="border-0 shadow-lg overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${method.color}`} />
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <h3 className="text-2xl font-medium">{method.name}</h3>
                    <div className="flex gap-2">
                      {method.youtubeUrl && (
                        <a 
                          href={method.youtubeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-sky-500 hover:bg-red-600 text-white rounded-lg transition-colors text-sm font-medium"
                        >
                          <Play size={16} />
                          YouTube
                        </a>
                      )}
                      {method.instagramUrl && (
                        <a 
                          href={method.instagramUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600 text-white rounded-lg transition-colors text-sm font-medium"
                        >
                          <Instagram size={16} />
                          Instagram
                        </a>
                      )}
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6 leading-relaxed">{method.description}</p>
                  <div className="rounded-xl p-4">
                    <h4 className="font-semibold text-slate-700 mb-3">Points clés :</h4>
                    <ul className="grid sm:grid-cols-2 gap-2">
                      {method.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-600">
                          <span className="text-sky-500 mt-1">✓</span>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Video Exercises */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
              Exercices Pratiques en Vidéo
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Des séquences guidées pour retrouver progressivement la mobilité de manière douce, 
              sans stresser le corps. Commencez par les exercices qui vous parlent le plus.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {exercises.map((exercise, index) => (
              <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-all group cursor-pointer">
                <CardContent className="p-6">
                  <div className="w-full h-32 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl mb-4 flex items-center justify-center group-hover:from-indigo-200 group-hover:to-purple-200 transition-colors">
                    <Play className="w-12 h-12 text-sky-500 group-hover:scale-110 transition-transform" />
                  </div>
                  <h3 className="font-bold text-lg text-slate-800 mb-2">{exercise.title}</h3>
                  <p className="text-slate-600 text-sm mb-3">{exercise.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">{exercise.duration}</span>
                    <a 
                      href={exercise.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-600 hover:text-sky-700 text-sm font-medium flex items-center gap-1"
                    >
                      Voir <ExternalLink size={14} />
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <div className="bg-white/60 backdrop-blur-md rounded-2xl p-8 border border-white/70 shadow-lg">
              <h3 className="text-xl font-medium mb-4 text-slate-900">Principes Essentiels</h3>
              <div className="grid sm:grid-cols-3 gap-6 text-left">
                <div>
                  <h4 className="font-semibold text-sky-600 mb-2">Douceur</h4>
                  <p className="text-slate-700 text-sm">Ne forcez jamais. Le corps s&apos;ouvre par la patience et la répétition douce, pas par la force.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sky-600 mb-2">Progression</h4>
                  <p className="text-slate-700 text-sm">Avancez à votre rythme. Chaque corps a son histoire et ses besoins propres.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sky-600 mb-2">Écoute</h4>
                  <p className="text-slate-700 text-sm">Développez la conscience de vos sensations. Le corps vous guide vers ce dont il a besoin.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Prêt à Libérer Votre Respiration ?
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
            La mobilité est le premier pas vers une respiration optimale. 
            Commencez dès aujourd&apos;hui à explorer le mouvement et ressentez la différence.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link 
              to="/app" 
              className="px-8 py-3 bg-cyan-500 text-white rounded-xl font-medium hover:bg-cyan-600 transition-all shadow-lg"
            >
              Accéder aux Exercices de Respiration
            </Link>
            <Link 
              to="/evaluer-respiration" 
              className="px-8 py-3 bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-800 transition-all shadow-lg"
            >
              Évaluer Votre Respiration
            </Link>
          </div>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default MobilityMovementPage;
